// Product UI mockups built in HTML/SVG. Fictional brands only (Monverta, Halvard, Fornell).
// Owner- and dealer-facing screens and a cropped brand view; never the admin navigation or data model.

import React from 'react';
import { Phone, Window, cx } from '../../ui';

/* ---------- fictional brand skins ---------- */

export type Skin = {
  key: string;
  name: string;
  domain: string;
  product: string;
  serial: string;
  ink: string;
  paper: string;
  accent: string;
  deep: string;
  art: 'vacuum' | 'heatpump';
  months: [number, number];
  actions: [string, string][];
};

export const SKINS: Record<'monverta' | 'halvard', Skin> = {
  monverta: {
    key: 'monverta',
    name: 'MONVERTA',
    domain: 'id.monverta.com',
    product: 'CleanLift A9',
    serial: 'CLA9-2024-00091',
    ink: '#2D2D2D',
    paper: '#F7F5F0',
    accent: '#D4A045',
    deep: '#2E4A3E',
    art: 'vacuum',
    months: [23, 24],
    actions: [
      ['Manual for your build', 'A9 · 2024 build · 3 min read'],
      ['Genuine parts', 'Filters, batteries, brush bars'],
      ['Report a problem', 'Photos and serial attached'],
      ['Ask about this unit', 'Answers from the A9 manual'],
    ],
  },
  halvard: {
    key: 'halvard',
    name: 'Halvard',
    domain: 'scan.halvardheat.co.uk',
    product: 'Halvard Air 9 heat pump',
    serial: 'KA9-25-118204',
    ink: '#14313B',
    paper: '#EEF3F4',
    accent: '#E2725B',
    deep: '#1F4E5F',
    art: 'heatpump',
    months: [79, 84],
    actions: [
      ['Commissioning record', 'Signed by your installer'],
      ['Book the annual service', 'Due March 2027'],
      ['Error codes explained', 'For this model and firmware'],
      ['Ask about this unit', 'Answers from the Air 9 manual'],
    ],
  },
};

function ProductArt({ skin }: { skin: Skin }) {
  if (skin.art === 'heatpump') {
    return (
      <svg viewBox="0 0 220 120" className="h-full w-full" aria-hidden="true">
        <rect x="40" y="16" width="140" height="92" rx="8" fill="#fff" stroke={skin.ink} strokeOpacity=".15" />
        <circle cx="92" cy="62" r="32" fill="none" stroke={skin.ink} strokeOpacity=".2" strokeWidth="2" />
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <line key={i} x1="92" y1="62" x2={92 + 30 * Math.cos((i * Math.PI) / 4)} y2={62 + 30 * Math.sin((i * Math.PI) / 4)} stroke={skin.ink} strokeOpacity=".18" strokeWidth="1.5" />
        ))}
        <circle cx="92" cy="62" r="6" fill={skin.deep} />
        <rect x="140" y="30" width="28" height="6" rx="3" fill={skin.accent} />
        <rect x="140" y="42" width="28" height="3" rx="1.5" fill={skin.ink} fillOpacity=".15" />
        <rect x="140" y="49" width="20" height="3" rx="1.5" fill={skin.ink} fillOpacity=".15" />
        <rect x="52" y="108" width="12" height="6" rx="2" fill={skin.ink} fillOpacity=".25" />
        <rect x="156" y="108" width="12" height="6" rx="2" fill={skin.ink} fillOpacity=".25" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 220 120" className="h-full w-full" aria-hidden="true">
      <rect x="104" y="6" width="12" height="20" rx="5" fill={skin.ink} />
      <rect x="107" y="24" width="6" height="58" rx="3" fill={skin.ink} fillOpacity=".85" />
      <rect x="96" y="44" width="28" height="30" rx="10" fill={skin.accent} />
      <rect x="101" y="50" width="18" height="8" rx="4" fill="#fff" fillOpacity=".55" />
      <rect x="107" y="80" width="6" height="22" rx="3" fill={skin.ink} fillOpacity=".7" />
      <rect x="78" y="100" width="64" height="12" rx="6" fill={skin.ink} />
      <rect x="84" y="104" width="52" height="4" rx="2" fill={skin.accent} fillOpacity=".7" />
    </svg>
  );
}

/* ---------- owner phone page ---------- */

export function OwnerScreen({ skin, compact = false }: { skin: Skin; compact?: boolean }) {
  const [left, total] = skin.months;
  return (
    <div className="flex h-full flex-col text-left" style={{ background: skin.paper, color: skin.ink, fontSize: 12 }}>
      <div className="flex items-center justify-between px-4 pb-2 pt-9">
        <span style={{ fontWeight: 800, letterSpacing: skin.key === 'monverta' ? '0.18em' : '-0.01em', fontSize: 12 }}>{skin.name}</span>
        <span className="rounded-full px-2 py-0.5" style={{ background: '#fff', fontSize: 9.5, color: skin.ink, opacity: 0.75 }}>
          Signed in
        </span>
      </div>
      <div className="mx-3 overflow-hidden rounded-2xl" style={{ background: '#fff' }}>
        <div className="h-[92px] px-6 pt-2" style={{ background: `linear-gradient(180deg, ${skin.paper}, #fff)` }}>
          <ProductArt skin={skin} />
        </div>
        <div className="px-3.5 pb-3.5 pt-1">
          <div style={{ fontWeight: 700, fontSize: 14.5, letterSpacing: '-0.01em' }}>{skin.product}</div>
          <div className="mt-0.5 font-mono" style={{ fontSize: 9.5, opacity: 0.6 }}>
            SN {skin.serial}
          </div>
          <div className="mt-2.5 rounded-xl px-3 py-2" style={{ background: skin.paper }}>
            <div className="flex items-center justify-between" style={{ fontSize: 10.5 }}>
              <span style={{ fontWeight: 600, color: skin.deep }}>✓ Warranty active</span>
              <span style={{ opacity: 0.6 }}>
                {left} of {total} months
              </span>
            </div>
            <div className="mt-1.5 h-[5px] overflow-hidden rounded-full" style={{ background: '#fff' }}>
              <div className="h-full rounded-full" style={{ width: `${(left / total) * 100}%`, background: skin.accent }} />
            </div>
          </div>
        </div>
      </div>
      <div className="mx-3 mt-2.5 overflow-hidden rounded-2xl" style={{ background: '#fff' }}>
        {skin.actions.slice(0, compact ? 3 : 4).map(([t, d], i) => (
          <div key={t} className="flex items-center justify-between px-3.5 py-2.5" style={{ borderTop: i ? `1px solid ${skin.paper}` : undefined }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 11.5 }}>{t}</div>
              <div style={{ fontSize: 9.5, opacity: 0.55 }}>{d}</div>
            </div>
            <span style={{ color: skin.accent, fontWeight: 700 }}>›</span>
          </div>
        ))}
      </div>
      <div className="mt-auto pb-3 text-center" style={{ fontSize: 8.5, opacity: 0.45 }}>
        Powered by Branded Mark
      </div>
    </div>
  );
}

export function OwnerPhone({ skin = SKINS.monverta, className }: { skin?: Skin; className?: string }) {
  return (
    <Phone className={className}>
      <OwnerScreen skin={skin} />
    </Phone>
  );
}

/* ---------- partner handover (Fornell bakery oven, signed off by the service partner) ---------- */

const PDI = ['Electrical safety check', 'Core probe calibrated', 'Steam system tested', 'Door seals checked', 'Staff trained'];

export function DealerScreen() {
  return (
    <div className="flex h-full flex-col bg-[#F7F3EE] text-left text-[#2A1F16]" style={{ fontSize: 12 }}>
      <div className="px-4 pb-3 pt-9" style={{ background: '#3B2716', color: '#fff' }}>
        <div className="flex items-center justify-between">
          <span style={{ fontWeight: 800, fontSize: 11.5, letterSpacing: '0.06em' }}>FORNELL</span>
          <span style={{ fontSize: 9.5, opacity: 0.7 }}>Northline Catering Services</span>
        </div>
        <div className="mt-3" style={{ fontWeight: 700, fontSize: 14.5 }}>
          Installation sign-off
        </div>
        <div className="font-mono" style={{ fontSize: 9.5, opacity: 0.75 }}>
          CX-10 combi oven · SN FCX10-26-00217
        </div>
      </div>
      <div className="flex items-center justify-between px-4 pb-1.5 pt-3" style={{ fontSize: 9.5, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.6 }}>
        <span>Sign-off checks</span>
        <span>5 of 5</span>
      </div>
      <div className="mx-3 overflow-hidden rounded-xl bg-white">
        {PDI.map((p, i) => (
          <div key={p} className="flex items-center gap-2.5 px-3 py-2" style={{ borderTop: i ? '1px solid #F0EBE4' : undefined }}>
            <span className="grid h-4 w-4 flex-none place-items-center rounded-[5px]" style={{ background: '#8A5A2B', color: '#fff', fontSize: 9 }}>
              ✓
            </span>
            <span style={{ fontSize: 11 }}>{p}</span>
          </div>
        ))}
      </div>
      <div className="mx-3 mt-2.5 rounded-xl bg-white px-3 py-2.5">
        <div style={{ fontSize: 9.5, opacity: 0.6 }}>Customer signature · Harbour Street Kitchen</div>
        <svg viewBox="0 0 200 40" className="mt-1 h-8 w-full" aria-hidden="true">
          <path d="M6 28c10-14 18-20 22-12s-6 16 2 12 12-20 20-16-4 18 6 14 14-10 22-8 6 10 14 6 10-12 20-10 14 6 26 2" fill="none" stroke="#2A1F16" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="4" y1="36" x2="196" y2="36" stroke="#2A1F16" strokeOpacity=".15" />
        </svg>
      </div>
      <div className="mx-3 mt-2.5 rounded-xl px-3 py-2.5" style={{ background: '#F1E6DA' }}>
        <div style={{ fontWeight: 700, fontSize: 11.5, color: '#6B4320' }}>Warranty starts today</div>
        <div style={{ fontSize: 9.5, opacity: 0.7 }}>19 Oct 2026 · sealed PDF filed on the unit</div>
      </div>
      <div className="mx-3 mb-4 mt-auto rounded-full py-2.5 text-center" style={{ background: '#3B2716', color: '#fff', fontWeight: 600, fontSize: 11.5 }}>
        Seal sign-off
      </div>
    </div>
  );
}

export function DealerPhone({ className }: { className?: string }) {
  return (
    <Phone className={className} dark>
      <DealerScreen />
    </Phone>
  );
}

/* ---------- brand view (cropped, record-level, not the admin IA) ---------- */

const UNITS: [string, string, string, string, 'ok' | 'case' | 'new'][] = [
  ['KAW9-26-04182', 'Halvard AW-9 heat pump', 'Brightwell Heating, Bristol', 'Today', 'new'],
  ['KAW9-26-04177', 'Halvard AW-9 heat pump', 'Hollis & Grant, Bath', 'Yesterday', 'ok'],
  ['KAW12-26-01180', 'Halvard AW-12 heat pump', 'Pennant Renewables, Cardiff', '16 Oct', 'case'],
  ['KAW9-26-04121', 'Halvard AW-9 heat pump', 'Brightwell Heating, Bristol', '14 Oct', 'ok'],
  ['KAW6-25-00977', 'Halvard AW-6 heat pump', 'Marsh Energy, Exeter', '12 Oct', 'ok'],
];

export function BrandConsole({ className, compact = false }: { className?: string; compact?: boolean }) {
  return (
    <Window className={className} title="halvard · units">
      <div className="grid text-left text-[12.5px] md:grid-cols-[1.45fr_1fr]">
        <div className="min-w-0 border-b border-[#E4E3F0] md:border-b-0 md:border-r">
          <div className="flex items-center justify-between px-5 pb-3 pt-4">
            <div>
              <div className="text-[14px] font-semibold text-[#241E61]">Handed over this week</div>
              <div className="text-[11.5px] text-[#67648F]">Every unit, owner and installer, by serial</div>
            </div>
            <span className="whitespace-nowrap rounded-full bg-[#F7F7FC] px-2.5 py-1 font-mono text-[10.5px] text-[#67648F]">5 units</span>
          </div>
          <div className="overflow-x-auto">
            <table className={cx('w-full', !compact && 'min-w-[440px]')}>
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-[0.08em] text-[#67648F]">
                  <th className="px-5 py-2 font-medium">Serial</th>
                  {!compact && <th className="px-2 py-2 font-medium">Installer</th>}
                  <th className="px-2 py-2 font-medium">Handover</th>
                  {!compact && <th className="px-5 py-2 text-right font-medium">Status</th>}
                </tr>
              </thead>
              <tbody>
                {UNITS.map(([sn, model, dealer, when, st]) => (
                  <tr key={sn} className={cx('border-t border-[#EEEDF5]', st === 'new' && 'bg-[#F5FBF2]')}>
                    <td className="px-5 py-2.5">
                      <div className="whitespace-nowrap font-mono text-[11.5px] text-[#232047]">{sn}</div>
                      <div className="whitespace-nowrap text-[10.5px] text-[#67648F]">{model}</div>
                    </td>
                    {!compact && <td className="whitespace-nowrap px-2 py-2.5 text-[#232047]">{dealer}</td>}
                    <td className="whitespace-nowrap px-2 py-2.5 text-[#67648F]">{when}</td>
                    {!compact && (
                    <td className="px-5 py-2.5 text-right">
                        <span
                          className="inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-medium"
                          style={
                            st === 'case'
                              ? { background: '#FFF4E5', color: '#9A5B00' }
                              : st === 'new'
                                ? { background: '#E5F4EA', color: '#2F9E58' }
                                : { background: '#F7F7FC', color: '#67648F' }
                          }
                        >
                          {st === 'case' ? 'Open case' : st === 'new' ? 'Warranty started' : 'Registered'}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-[#FBFBFE] p-5">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.08em] text-[#67648F]">Case 2291</div>
            <span className="whitespace-nowrap rounded-full bg-[#FFF4E5] px-2 py-0.5 text-[10.5px] font-medium text-[#9A5B00]">Awaiting decision</span>
          </div>
          <div className="mt-2 text-[14px] font-semibold text-[#241E61]">Fan not restarting after defrost</div>
          <dl className="mt-3 space-y-1.5 text-[11.5px]">
            {[
              ['Serial', 'KAW12-26-01180'],
              ['Build', 'January 2026'],
              ['Commissioned', '4 Mar 2026'],
              ['Installer', 'Pennant Renewables'],
              ['Service', '1 visit, 11 Sep 2026'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3">
                <dt className="text-[#67648F]">{k}</dt>
                <dd className="text-right text-[#232047]">{v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-3 flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-10 flex-1 rounded-md" style={{ background: ['#DDE3E2', '#CCD6D4', '#E6EAE9'][i] }} />
            ))}
          </div>
          <div className="mt-1.5 text-[10.5px] text-[#67648F]">3 photos from the owner's scan</div>
          <div className="mt-4 grid grid-cols-3 gap-1.5 text-center text-[11px] font-semibold">
            <span className="rounded-full bg-[#241E61] py-1.5 text-white">Approve</span>
            <span className="rounded-full py-1.5 text-[#241E61] shadow-[inset_0_0_0_1px_#E4E3F0]">Return</span>
            <span className="rounded-full py-1.5 text-[#241E61] shadow-[inset_0_0_0_1px_#E4E3F0]">Decline</span>
          </div>
        </div>
      </div>
    </Window>
  );
}

/* ---------- the life of one unit (platform hero) ---------- */

const LIFE: { d: string; t: string; s: string; who?: string }[] = [
  { d: '12 Feb 2026', t: 'Built', s: 'Lot L2602-21, from your ERP' },
  { d: '20 Feb 2026', t: 'Shipped to installer', s: 'Brightwell Heating, Bristol' },
  { d: '09 Mar 2026', t: 'Commissioned', s: 'Signed on a phone, warranty starts', who: 'Owner 1' },
  { d: '02 Apr 2026', t: 'Owner asked', s: 'Flow temperature, manual p.18' },
  { d: '14 Sep 2026', t: 'Serviced', s: 'Annual service, genuine filter fitted' },
  { d: '03 Oct 2026', t: 'House sold', s: 'New owners take over, history kept', who: 'Owner 2' },
];

export function UnitLife({ className, animate = true }: { className?: string; animate?: boolean }) {
  return (
    <div className={cx('pk-card overflow-hidden', className)}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 border-b border-bm-line px-5 py-4 sm:px-6">
        <div className="min-w-0">
          <div className="text-[13px] font-extrabold uppercase tracking-[0.12em]" style={{ color: '#2E6F6A' }}>Halvard</div>
          <div className="mt-0.5 text-[17px] font-bold tracking-[-0.015em] text-bm-indigo">AW-9 air source heat pump</div>
          <div className="font-mono text-[12px] text-bm-muted">SN KAW9-26-04182</div>
        </div>
        <div className="flex-none rounded-xl bg-bm-alt px-3 py-2 sm:text-right">
          <div className="text-[11px] text-bm-muted">Warranty</div>
          <div className="text-[13px] font-semibold text-bm-emerald">Active to Mar 2031</div>
        </div>
      </div>
      <ol className="relative px-5 py-4 sm:px-6">
        <span aria-hidden="true" className="absolute bottom-7 left-[calc(1.25rem+5px)] top-7 w-px bg-bm-line sm:left-[calc(1.5rem+5px)]" />
        {LIFE.map((e, i) => (
          <li
            key={e.t}
            className={cx('relative grid grid-cols-[12px_1fr_auto] items-start gap-x-4 py-2.5', animate && 'pk-rise')}
            style={animate ? { animationDelay: `${0.25 + i * 0.16}s` } : undefined}
          >
            <span
              className="relative z-[1] mt-[5px] h-[11px] w-[11px] rounded-full"
              style={i === LIFE.length - 1 ? { background: 'var(--bm-gradient)' } : { background: '#fff', boxShadow: 'inset 0 0 0 2px var(--bm-indigo)' }}
            />
            <div className="min-w-0">
              <div className="text-[14.5px] font-semibold text-bm-indigo">{e.t}</div>
              <div className="text-[13px] leading-snug text-bm-muted">{e.s}</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[11.5px] text-bm-muted">{e.d}</div>
              {e.who && <div className="mt-1 inline-block rounded-full bg-bm-indigo px-2 py-0.5 text-[10.5px] font-semibold text-white">{e.who}</div>}
            </div>
          </li>
        ))}
      </ol>
      <div className="flex items-center justify-between gap-4 border-t border-bm-line bg-bm-alt px-5 py-3 text-[12.5px] sm:px-6">
        <span className="text-bm-muted">One record, from factory to the next owners</span>
        <span className="whitespace-nowrap font-mono text-bm-indigo">6 events</span>
      </div>
    </div>
  );
}

/* ---------- GS1 Digital Link, annotated ---------- */

export function DigitalLink({ dark = false }: { dark?: boolean }) {
  const parts: [string, string][] = [
    ['https://id.monverta.com', 'Your domain'],
    ['/01/05065001234569', 'GTIN: the product'],
    ['/21/CLA9-2024-00091', 'Serial: this unit'],
  ];
  return (
    <div className={cx('rounded-2xl p-4 sm:p-5', dark ? 'bg-white/[0.06]' : 'bg-bm-alt')}>
      <div className="flex flex-wrap gap-y-3 font-mono text-[12.5px] sm:text-[13.5px]">
        {parts.map(([v, k], i) => (
          <div key={v} className="min-w-0">
            <div className={cx('break-all px-0.5 pb-1.5', dark ? 'text-white' : 'text-bm-indigo')} style={{ borderBottom: `2px solid ${['#3FAE6C', '#8FD53A', dark ? '#CFF710' : '#241E61'][i]}` }}>
              {v}
            </div>
            <div className={cx('mt-1.5 font-display text-[11.5px]', dark ? 'text-white/60' : 'text-bm-muted')}>{k}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- passport view (fictional precision supplier) ---------- */

export function PassportScreen() {
  return (
    <div className="flex h-full flex-col bg-[#F5F6F8] text-left text-[#1B2430]" style={{ fontSize: 12 }}>
      <div className="flex items-center justify-between px-4 pb-2 pt-9">
        <span style={{ fontWeight: 800, fontSize: 11.5, letterSpacing: '0.12em' }}>OSTRAND</span>
        <span className="rounded-full bg-white px-2 py-0.5" style={{ fontSize: 9.5, opacity: 0.75 }}>
          Public view
        </span>
      </div>
      <div className="mx-3 rounded-2xl bg-white px-3.5 py-3">
        <div style={{ fontSize: 9.5, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.55 }}>Product passport</div>
        <div className="mt-1" style={{ fontWeight: 700, fontSize: 14 }}>
          Machined housing, 6082-T6
        </div>
        <div className="font-mono" style={{ fontSize: 9.5, opacity: 0.6 }}>
          Lot L26-0418 · delivery 7731
        </div>
      </div>
      <div className="mx-3 mt-2.5 rounded-2xl bg-white px-3.5 py-3">
        <div style={{ fontWeight: 600, fontSize: 11.5 }}>Material</div>
        <div className="mt-2 flex h-2 overflow-hidden rounded-full">
          <span style={{ width: '97%', background: '#3B5B7A' }} />
          <span style={{ width: '3%', background: '#9DB4C8' }} />
        </div>
        <div className="mt-1.5 flex justify-between" style={{ fontSize: 9.5, opacity: 0.65 }}>
          <span>Aluminium alloy EN AW-6082</span>
          <span>Coating</span>
        </div>
      </div>
      <div className="mx-3 mt-2.5 overflow-hidden rounded-2xl bg-white">
        {[
          ['Inspection certificate', 'EN 10204 3.1 · PDF'],
          ['Declaration of conformity', 'PDF'],
          ['Care and disposal', 'Recycling guidance'],
        ].map(([t, d], i) => (
          <div key={t} className="flex items-center justify-between px-3.5 py-2.5" style={{ borderTop: i ? '1px solid #EEF0F3' : undefined }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 11.5 }}>{t}</div>
              <div style={{ fontSize: 9.5, opacity: 0.55 }}>{d}</div>
            </div>
            <span style={{ color: '#3B5B7A', fontWeight: 700 }}>›</span>
          </div>
        ))}
      </div>
      <div className="mx-3 mt-2.5 rounded-2xl px-3.5 py-2.5" style={{ background: '#E7EEF4' }}>
        <div style={{ fontWeight: 600, fontSize: 11 }}>Something wrong with this delivery?</div>
        <div style={{ fontSize: 9.5, opacity: 0.65 }}>Report it here. The lot and certificate go with it.</div>
      </div>
      <div className="mt-auto pb-3 text-center" style={{ fontSize: 8.5, opacity: 0.45 }}>
        Powered by Branded Mark
      </div>
    </div>
  );
}

/* ---------- parts for this unit (Halvard heat pump, owner view in the maker's brand) ---------- */

const HALVARD = { ink: '#14313B', paper: '#F3F5F4', accent: '#2E6F6A' };
const PART_FALLBACK = '/images/v2/gen/heatpump-commission-b.jpg';

function UnitImg({ src, className, style }: { src: string; className?: string; style?: React.CSSProperties }) {
  const [failed, setFailed] = React.useState(false);
  return <img src={failed ? PART_FALLBACK : src} onError={() => setFailed(true)} alt="" width={640} height={640} className={className} style={{ objectFit: 'cover', ...style }} />;
}

export function PartsScreen({ unit, part }: { unit: { brand: string; model: string; serial: string; partner: string; product: string }; part: { part: string; price: string; by: string } }) {
  const rows: [string, string][] = [
    ['Diverter valve', 'Spare, fits this serial'],
    ['Wall controller', 'Spare, fits this build'],
    ['Anti-vibration feet', 'Accessory, fits the AW range'],
  ];
  return (
    <div className="flex h-full flex-col text-left" style={{ background: HALVARD.paper, color: HALVARD.ink, fontSize: 12 }}>
      <div className="flex items-center justify-between px-4 pb-2 pt-9">
        <span className="flex items-center gap-1.5">
          <span className="grid h-[18px] w-[18px] place-items-center rounded-[5px] text-white" style={{ background: HALVARD.accent, fontSize: 10, fontWeight: 800 }}>
            {unit.brand[0]}
          </span>
          <span style={{ fontWeight: 800, letterSpacing: '0.16em', fontSize: 11, color: HALVARD.accent, textTransform: 'uppercase' }}>{unit.brand}</span>
        </span>
        <span className="font-mono" style={{ fontSize: 9, opacity: 0.6 }}>
          {unit.serial}
        </span>
      </div>
      <div className="mx-3 flex items-center gap-3 rounded-2xl bg-white p-2.5">
        <UnitImg src={unit.product} className="h-12 w-12 flex-none rounded-xl" />
        <div className="min-w-0">
          <div style={{ fontWeight: 700, fontSize: 13.5, letterSpacing: '-0.01em' }}>Parts for your {unit.model.replace(`${unit.brand} `, '')}</div>
          <div style={{ fontSize: 9.5, opacity: 0.6 }}>Only what fits this unit</div>
        </div>
      </div>
      <div className="mx-3 mt-2.5 rounded-2xl bg-white p-3" style={{ boxShadow: `inset 0 0 0 1.5px ${HALVARD.accent}` }}>
        <div className="flex items-center justify-between">
          <span style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: HALVARD.accent, fontWeight: 700 }}>Suggested for E21</span>
          <span style={{ fontSize: 9, opacity: 0.55 }}>Illustrative price</span>
        </div>
        <div className="mt-1.5" style={{ fontWeight: 700, fontSize: 13 }}>
          {part.part}
        </div>
        <div className="font-mono" style={{ fontSize: 9, color: '#2F9E58' }}>
          ✓ Fits {unit.serial}
        </div>
        <div className="mt-2 flex items-end justify-between">
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>{part.price}</div>
            <div style={{ fontSize: 9.5, opacity: 0.6 }}>Fitted by {unit.partner}</div>
          </div>
          <span className="rounded-full px-3.5 py-1.5 text-white" style={{ background: HALVARD.accent, fontSize: 11, fontWeight: 600 }}>
            Order
          </span>
        </div>
      </div>
      <div className="mx-3 mt-2.5 overflow-hidden rounded-2xl bg-white">
        {rows.map(([t, d], i) => (
          <div key={t} className="flex items-center justify-between px-3.5 py-2.5" style={{ borderTop: i ? `1px solid ${HALVARD.paper}` : undefined }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 11.5 }}>{t}</div>
              <div style={{ fontSize: 9.5, opacity: 0.55 }}>{d}</div>
            </div>
            <span style={{ color: HALVARD.accent, fontWeight: 700 }}>›</span>
          </div>
        ))}
      </div>
      <div className="mx-3 mt-2.5 rounded-2xl px-3.5 py-2.5" style={{ background: '#E4EEEC' }}>
        <div style={{ fontWeight: 600, fontSize: 11 }}>Filters due at your next service</div>
        <div style={{ fontSize: 9.5, opacity: 0.65 }}>Reorder in one tap. Same address.</div>
      </div>
      <div className="mt-auto pb-3 text-center" style={{ fontSize: 8.5, opacity: 0.4 }}>
        Powered by BrandedMark
      </div>
    </div>
  );
}

/* ---------- a sealed form: the signed page with its audit page behind ---------- */

const SIG_1 = 'M4 26 C 8 10, 14 4, 18 12 C 22 20, 14 30, 22 28 C 30 26, 32 12, 38 14 C 43 16, 38 26, 44 26 C 50 26, 52 16, 56 16 C 60 16, 58 24, 63 24 C 68 24, 72 12, 78 12 M 10 32 C 40 30, 70 28, 96 24';
const SIG_2 = 'M6 24 C 12 8, 20 8, 18 20 C 16 30, 10 30, 15 24 C 22 16, 28 18, 30 24 C 32 29, 37 29, 40 21 C 42 15, 46 16, 46 22 C 46 27, 51 27, 54 21 L 57 14 C 59 24, 61 28, 66 24 C 71 21, 73 16, 78 18';

export function SealedForm({ className, doc = 'Commissioning certificate', brand = 'Halvard', serial = 'KAW9-26-04182', signers = [['Installer', 'Brightwell Heating'], ['Homeowner', '14 Orchard Lane']] as [string, string][], checks = ['Refrigerant pressures checked', 'Flow temperature set', 'Controls paired', 'Homeowner walkthrough'] }: { className?: string; doc?: string; brand?: string; serial?: string; signers?: [string, string][]; checks?: string[] }) {
  return (
    <div className={cx('relative mx-auto w-full max-w-[460px] pb-14 text-left sm:pl-8', className)} aria-hidden="true">
      {/* a page behind, so it reads as a document */}
      <div className="absolute bottom-10 left-3 right-[-12px] top-4 rounded-[14px] bg-[#FBFBFD] sm:left-11" style={{ boxShadow: '0 0 0 1px #E4E3F0' }} />
      {/* the signed page */}
      <div className="relative rounded-[14px] bg-white p-5 sm:p-6" style={{ boxShadow: '0 0 0 1px #E4E3F0, 0 30px 60px -30px rgba(24,19,67,.45)' }}>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            <span className="grid h-5 w-5 place-items-center rounded-[5px] text-[10.5px] font-extrabold text-white" style={{ background: HALVARD.accent }}>
              {brand[0]}
            </span>
            <span className="text-[11px] font-extrabold uppercase tracking-[0.16em]" style={{ color: HALVARD.accent }}>
              {brand}
            </span>
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(63,174,108,.12)] px-2.5 py-1 text-[11px] font-semibold text-bm-emerald">
            <svg width="10" height="11" viewBox="0 0 9 10" fill="none" aria-hidden="true">
              <path d="M4.5 1 8 2.4v2.8c0 2-1.5 3.4-3.5 3.9C2.5 8.6 1 7.2 1 5.2V2.4z" stroke="currentColor" strokeWidth="1.2" />
            </svg>
            Sealed
          </span>
        </div>
        <div className="mt-4 text-[20px] font-bold leading-tight tracking-[-0.02em] text-bm-indigo">{doc}</div>
        <div className="mt-1 font-mono text-[11px] text-bm-muted">{serial}</div>
        <ul className="mt-4 border-t border-bm-line">
          {checks.map((c) => (
            <li key={c} className="flex items-center gap-2.5 border-b border-bm-line py-2 text-[13px] text-bm-ink">
              <span className="grid h-4 w-4 flex-none place-items-center rounded-[4px]" style={{ background: HALVARD.accent }}>
                <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                  <path d="M3 7.4 5.8 10 11 4.2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              {c}
            </li>
          ))}
        </ul>
        <div className="mt-4 grid grid-cols-2 gap-3">
          {signers.map(([role, name], i) => (
            <div key={role}>
              <div className="h-[46px] rounded-lg bg-bm-alt px-2 pt-1" style={{ boxShadow: 'inset 0 0 0 1px #E4E3F0' }}>
                <svg viewBox="0 0 100 36" className="h-full w-full">
                  <path d={i ? SIG_2 : SIG_1} fill="none" stroke="#241E61" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="mt-1.5 font-mono text-[9.5px] uppercase tracking-[0.12em] text-bm-muted">{role}</div>
              <div className="truncate text-[12px] font-semibold text-bm-indigo">{name}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-end border-t border-bm-line pt-3 pb-1">
          <span className="rounded-full px-3 py-1 text-[11.5px] font-semibold text-bm-indigo" style={{ boxShadow: 'inset 0 0 0 1px #E4E3F0' }}>
            Verify
          </span>
        </div>
      </div>
      {/* the audit page */}
      <div className="absolute bottom-0 left-0 w-[250px] rounded-[12px] bg-bm-indigo px-4 py-3.5 text-white" style={{ boxShadow: '0 24px 40px -20px rgba(24,19,67,.6)' }}>
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-bm-lime">Audit page</div>
        <div className="mt-2 space-y-1 font-mono text-[10.5px] leading-snug text-white/75">
          <div>Signed 14:32 and 14:33, on site</div>
          <div>Consent text shown · v3</div>
          <div>Hash 9f2c 71ab … e41a</div>
          <div>Filed on {serial}</div>
        </div>
      </div>
    </div>
  );
}
